import { Inject, inject, Injectable } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { APP_STORAGE } from '~/app.token';

import { ErrorCodesEnum } from '~/core/services/error-code/enums/codes.enum';
import { ErrorCodeService } from '~/core/services/error-code/error-code.service';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { OrganizerSharedAbstractGuard } from '~/organizer/shared/guards/guard.abstract';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';

const CURRENT_ORGANIZATION = 'currentOrganization';

@Injectable({
  providedIn: 'root',
})
class PermissionsService extends OrganizerSharedAbstractGuard {
  protected readonly errorCode = ErrorCodesEnum.OrganizerSharedHasOrganization;

  constructor(
    @Inject(APP_STORAGE) private readonly storage: Storage,
    private readonly organizationService: OrganizerSharedOrganizationService,
    protected readonly errorCodeService: ErrorCodeService,
    protected readonly router: Router,
    private readonly redirectionService: RedirectionService
  ) {
    super(errorCodeService, router);
  }

  public canActivate(): Observable<boolean | UrlTree> {
    return this.organizationService.getOrganizationIdsFromAuthUser().pipe(
      switchMap((organizationIds) =>
        this.handleOrganizationIds(organizationIds)
      ),
      catchError(() => this.handleError())
    );
  }

  private handleOrganizationIds(
    organizationIds: string[]
  ): Observable<boolean | UrlTree> {
    if (!organizationIds?.length) {
      return of(true);
    }

    const storedOrganization = this.getStoredOrganization();
    const hasMultipleOrganizations = organizationIds?.length > 1;

    if (hasMultipleOrganizations && !storedOrganization) {
      this.redirectionService.redirect('organizer.organization-choice');
      return of(false);
    }

    const identifier = hasMultipleOrganizations
      ? storedOrganization
      : organizationIds?.[0];

    return this.organizationService.getOrganizationById(identifier).pipe(
      map((org) => organizationIds.includes(org?.identifier ?? '')),
      catchError(() => this.handleError())
    );
  }

  private getStoredOrganization(): string | null {
    const storedItem = this.storage.getItem(CURRENT_ORGANIZATION);
    if (!storedItem) {
      return null;
    }

    try {
      return JSON.parse(storedItem).organization.split('/organizations/')[1];
    } catch {
      return null;
    }
  }

  private handleError(): Observable<boolean> {
    this.catchError();
    return of(false);
  }
}

export const OrganizerSharedHasOrganizationGuard: CanActivateFn = () => {
  return inject(PermissionsService).canActivate();
};
